import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Alert, FloatingLabel } from "react-bootstrap";
import useAxios from "../../services/AxiosConfig";
import { useSelector, useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import styles from './Appointment.module.css';
import { AddTimeIcon, TrashIcon } from "../../components/icons/Icons";
import ManagementStyles from '../ManagementPages.module.css';
import { showAlert } from '../../components/PerinHealthAlertSlice'; // Import showAlert

const daysOfWeek = [
  { name: "Monday", value: 1 },
  { name: "Tuesday", value: 2 },
  { name: "Wednesday", value: 3 },
  { name: "Thursday", value: 4 },
  { name: "Friday", value: 5 },
  { name: "Saturday", value: 6 },
  { name: "Sunday", value: 0 },
];

const convertTo24Hour = (time12h) => {
  const [time, modifier] = time12h.split(" ");
  let [hours, minutes] = time.split(":");
  if (hours === "12") {
    hours = "00";
  }
  if (modifier === "PM") {
    hours = parseInt(hours, 10) + 12;
  }
  return `${hours}:${minutes}`;
};

const convertTo12Hour = (time24h) => {
  let [hours, minutes] = time24h.split(":");
  hours = parseInt(hours, 10);
  const modifier = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${modifier}`;
};

const generateTimeOptions = () => {
  const options = [];
  for (let hour = 6; hour < 24; hour++) {
    const time12h = `${hour % 12 || 12}:00 ${hour < 12 ? "AM" : "PM"}`;
    options.push(
      <option key={time12h} value={time12h}>
        {time12h}
      </option>
    );
  }
  options.push(
    <option key="11:59 PM" value="11:59 PM">
      11:59 PM
    </option>
  );
  
  return options;
};

const AvailabilityComponent = ({
  show,
  onHide,
  selectedPhysicianId,
  physicians,
  role,
  canManageAvailability
}) => {
  const [availability, setAvailability] = useState([]);
  const [validationError, setValidationError] = useState(false);
  const userInfo = useSelector((state) => state.userInfo);

  const allowedRoles = ["Group Admin", "Super Admin"];
  const isSuperAdmin = userInfo.isAdmin || userInfo.roleName === "Super Admin";
  const showPhysicianSelection = allowedRoles.includes(role) || canManageAvailability || isSuperAdmin;
  const canSetAvailabilityForSelf = userInfo.canHaveAppointment !== false || isSuperAdmin;

  const [selectedPhysician, setSelectedPhysician] = useState(
    showPhysicianSelection ? "" : (canSetAvailabilityForSelf ? selectedPhysicianId : "")
  );

  const dispatch = useDispatch();
  const axiosConfig = useSelector((state) => state.axiosConfig);
  const httpService = useAxios(axiosConfig.envURL);

  const fetchAvailability = async () => {
    try {
      if (!selectedPhysician) return; // Prevent fetching if no physician is selected
      if (!showPhysicianSelection && !canSetAvailabilityForSelf && !isSuperAdmin) {
        setAvailability([]);
        return; // User doesn't have canHaveAppointment, can't set availability for themselves
      }

      const response = await httpService.get(
        `appointment/availability/${selectedPhysician}`
      );
      if (response.data && Array.isArray(response.data.data)) {
        const fetchedAvailability = response.data.data.map((item) => {
          const dayNum = Number(item.day);
          const dayObj = daysOfWeek.find((d) => d.value === dayNum);
          return {
            ...item,
            start: convertTo12Hour(item.start),
            end: convertTo12Hour(item.end),
            day: dayObj?.name ?? String(item.day),
            dayValue: dayObj?.value ?? dayNum,
          };
        });
        setAvailability(fetchedAvailability);
      } else {
        setAvailability([]);
        console.log("No availability data found or data format incorrect");
      }
    } catch (error) {
      console.error("Error fetching availability:", error);
      setValidationError(true);
    }
  };

  useEffect(() => {
    fetchAvailability();
  }, [selectedPhysician, httpService]);

  const addTimeSlot = (dayValue) => {
    const dayName = daysOfWeek.find((day) => day.value === dayValue).name;
    const newSlot = {
      day: dayName,
      dayValue: dayValue, // For saving
      start: "",
      end: "",
      physicianAvailabilityId: uuidv4(),
    };
    setAvailability((prev) => [...prev, newSlot]);
  };

  const handleTimeChange = (id, field, value) => {
    setAvailability((prev) =>
      prev.map((slot) =>
        slot.physicianAvailabilityId === id ? { ...slot, [field]: value } : slot
      )
    );
  };

  const deleteTimeSlot = (id) => {
    setAvailability((prev) =>
      prev.filter((slot) => slot.physicianAvailabilityId !== id)
    );
  };

  const handleCheckboxChange = (dayValue) => {
    if (availability.some((item)=> item.day === dayValue.name)) {
      setAvailability((prev) =>
        prev.filter((slot) => slot.day !== dayValue.name)
      );
    } else {
      const dayName = daysOfWeek.find((day) => day.value === dayValue.value).name;
      const newSlot = {
        day: dayName,
        dayValue: dayValue.value, // For saving
        start: "",
        end: "",
        physicianAvailabilityId: uuidv4(),
      };
      setAvailability((prev) => [...prev, newSlot]);
    }
  };

  const handleSubmit = () => {
    if (!selectedPhysician) {
      alert("Please select a physician before submitting.");
      return;
    }
    if (!showPhysicianSelection && !canSetAvailabilityForSelf && !isSuperAdmin) {
      dispatch(
        showAlert({
          header: "Error",
          message: "You do not have appointment eligibility enabled. Please contact an administrator.",
          type: 'danger',
        })
      );
      return;
    }

    const isValid = availability.every((slot) => {
      const start = new Date(`1970/01/01 ${convertTo24Hour(slot.start)}`);
      const end = new Date(`1970/01/01 ${convertTo24Hour(slot.end)}`);
      return start < end;
    });

    if (!isValid) {
      setValidationError(true);
      return;
    } else {
      setValidationError(false);
    }

    const submissionData = availability.map((slot) => ({
      physicianAvailabilityId: slot.physicianAvailabilityId ?? uuidv4(),
      day: slot.day,
      start: convertTo24Hour(slot.start),
      end: convertTo24Hour(slot.end),
    }));

    const url = `appointment/availability/${selectedPhysician}`;
    const method = "POST";

    httpService
      .request({
        method,
        url,
        data: { availabilities: submissionData },
      })
      .then((response) => {
        dispatch(
          showAlert({
            header: "Success",
            message: "Availability saved successfully",
            type: 'success',
          })
        );
        onHide();
      })
      .catch((error) => {
        console.error("Error saving data:", error);
        dispatch(
          showAlert({
            header: "Error",
            message: "Failed to save availability",
            type: 'danger',
          })
        );
      });
  };

  const handleCancel = async () => {
    setValidationError(false);
    await fetchAvailability();
    onHide();
  }

  return (
    <Modal style={{fontFamily:"Roboto"}} show={show} onHide={handleCancel} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{fontSize:"20px", color:"#4F4F4F", fontWeight:"700", letterSpacing:"0.03px"}}>Setup Availability</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{fontSize:"16px", height:"450px", display:"flex"}}>
        <div style={{flexGrow:"1", overflowY:"scroll", overflowX:"hidden"}}>

        {showPhysicianSelection && (
          <>
          <Form.Group as={Col} controlId="formGridPhysician">
            <Form.Select
              value={selectedPhysician}
              onChange={(e) => setSelectedPhysician(e.target.value)}
            >
              <option value="">Select a Physician</option>
              {physicians.map((physician) => (
                <option key={physician.value} value={physician.value}>
                  {physician.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <br />
          </>
        )}
        {!showPhysicianSelection && !canSetAvailabilityForSelf && !isSuperAdmin && (
          <Alert variant="warning">
            You do not have appointment eligibility enabled. Please contact an administrator to enable appointment scheduling for your account.
          </Alert>
        )}
        {validationError && (
          <Alert variant="danger">
            Please ensure all end times are after start times and all time slots
            are filled correctly.
          </Alert>
        )}
        {daysOfWeek.map((day, index) => {
          const timeSelections = availability.filter((slot) => slot.day === day.name);
          return (
          <React.Fragment key={`${day.name}-${index}`}>
            <Row className={`align-items-start ${index!==0 && "mt-3"}`}>
              <Col xs={1}>
                <div key={day} className="permission-checkbox" style={{height:"44px"}}>
                    <input
                        type="checkbox"
                        checked={availability.some((item)=> item.day === day.name)}
                        onChange={() => handleCheckboxChange(day)}
                    />
                </div>
              </Col>
              <Col xs={2}>
                <div className="subtitle-2" style={{color:"#4F4F4F", height:"44px", display:"flex", alignItems:"center"}}>{day.name}</div>
              </Col>
              <Col xs={6}>
                
                {timeSelections[0] &&
                  <Row
                    key={timeSelections[0].physicianAvailabilityId}
                    className="align-items-start mb-2"
                  >
                    <Col xs={9} className="offset-xs-2 d-flex">
                    <FloatingLabel label="Start Time">
                      <Form.Select
                        className={ManagementStyles.BorderedComponent}
                        style={{marginRight:"10px", width:"123px"}}
                        value={timeSelections[0].start}
                        onChange={(e) =>
                          handleTimeChange(
                            timeSelections[0].physicianAvailabilityId,
                            "start",
                            e.target.value
                          )
                        }
                      >
                        <option value=""></option>
                        {generateTimeOptions()}
                      </Form.Select>
                      </FloatingLabel>
                      <FloatingLabel label="End Time">
                        <Form.Select
                          className={ManagementStyles.BorderedComponent}
                          style={{width:"123px"}}
                          value={timeSelections[0].end}
                          onChange={(e) =>
                            handleTimeChange(
                              timeSelections[0].physicianAvailabilityId,
                              "end",
                              e.target.value
                            )
                          }
                        >
                          <option value=""></option>
                          {generateTimeOptions()}
                        </Form.Select>
                      </FloatingLabel>
                    </Col>
                    <Col xs={3}>
                    </Col>
                  </Row>
                }
                {timeSelections.length === 0 && (
                  <Row
                    className="align-items-start mb-2"
                  >
                    <Col xs={9} className="offset-xs-2 d-flex">
                      <Form.Select
                        style={{marginRight:"10px"}}
                        className={styles.timeSelect}
                        value={""}
                        disabled={true}
                      >
                        <option value="">Start Time</option>
                      </Form.Select>
                      <Form.Select
                        className={styles.timeSelect}
                        value={""}
                        disabled={true}
                      >
                        <option value="">End Time</option>
                      </Form.Select>
                    </Col>
                  </Row>
                )}
              </Col>
              <Col xs={3} className="text-end">
                <Button
                  className={styles.AddTimeSlotButton}
                  variant="outline-primary"
                  size="sm"
                  disabled={timeSelections.length === 0}
                  onClick={() => addTimeSlot(day.value)}
                >
                  <div style={{display:"flex", alignItems:"center", columnGap:"10px"}}>
                    <AddTimeIcon color={timeSelections.length === 0 ? "#B9B9B9" :"#1D5769"}/>
                    Add Time
                  </div>
                </Button>
              </Col>
            </Row>
            {timeSelections.slice(1).map((slot, index) =>
              <Row key={`${slot.physicianAvailabilityId}-${index}`} className={`align-items-start ${index!==0 && "mt-2"}`}>
                <Col xs={3}>
                </Col>
                <Col xs={6} className="offset-xs-2 d-flex">
                  <FloatingLabel label="Start Time">
                    <Form.Select
                      className={ManagementStyles.BorderedComponent}
                      style={{marginRight:"10px", width:"123px"}}
                      value={slot.start}
                      onChange={(e) =>
                        handleTimeChange(
                          slot.physicianAvailabilityId,
                          "start",
                          e.target.value
                        )
                      }
                    >
                      <option value=""></option>
                      {generateTimeOptions()}
                    </Form.Select>
                  </FloatingLabel>
                  <FloatingLabel label="End Time">
                    <Form.Select
                      className={ManagementStyles.BorderedComponent}
                      style={{ width:"123px"}}
                        value={slot.end}
                        onChange={(e) =>
                          handleTimeChange(
                            slot.physicianAvailabilityId,
                            "end",
                            e.target.value
                          )
                        }
                      >
                        <option value=""></option>
                        {generateTimeOptions()}
                      </Form.Select>
                      </FloatingLabel>
                    </Col>
                    <Col xs={3}>
                      <Button
                        className={styles["delete-button"]}
                        variant="danger"
                        size="sm"
                        onClick={() =>
                          deleteTimeSlot(slot.physicianAvailabilityId)
                        }
                      >
                        <TrashIcon />
                      </Button>
                    </Col>
              </Row>
            )}
            
          </React.Fragment>
        )})}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button className={ManagementStyles.BackButton} variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button className={ManagementStyles.CreateButton} variant="primary" onClick={handleSubmit}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AvailabilityComponent;
